<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'

const {
    ModalContainer,
    Button: PocketButton,
} = UIKit

withDefaults(defineProps<{
    numAnswers?: number
    quizLength?: number
    isRecallChallengeQuiz?: boolean
}>(), {
    numAnswers: 0,
    quizLength: 1,
    isRecallChallengeQuiz: false,
})

const submitQuizModalTitle = ref<HTMLElement>()

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const emit = defineEmits<{
    close: []
    continue: []
    submit: []
}>()

onMounted(() => {
    nextTick(() => {
        const titleEl = submitQuizModalTitle.value
        titleEl?.focus()
    })
})

const emitClose = () => {
    emit('close')
}

const emitContinue = () => {
    emit('continue')
}

const emitSubmit = () => {
    emit('submit')
}
</script>

<template>
    <Portal to="modal">
        <ModalContainer
            key="submitQuizModal"
            :show-close-button="true"
            :is-dark-mode="isDarkMode"
            @close="emitClose"
        >
            <div class="submit-quiz-modal">
                <h1
                    ref="submit-quiz-modal__title"
                    class="submit-quiz-modal__title"
                    tabindex="-1"
                >
                    Submit Quiz?
                </h1>
                <div 
                    v-if="isRecallChallengeQuiz" 
                    v-dark 
                    class="submit-quiz-modal__text"
                >
                    You've answered {{ numAnswers }} of {{ quizLength }} question{{
                        quizLength !== 1 ? 's' : ''
                    }}. If you submit, unanswered questions will be marked incorrect.
                </div>
                <div 
                    v-else 
                    v-dark 
                    class="submit-quiz-modal__text"
                >
                    You've answered {{ numAnswers }} of {{ quizLength }} question{{
                        quizLength !== 1 ? 's' : ''
                    }}. If you submit now, you'll only be scored on the {{ numAnswers }} question{{
                        numAnswers !== 1 ? 's' : ''
                    }} you've answered.
                </div>
                <div class="submit-quiz-modal__actions">
                    <PocketButton
                        type="secondary"
                        class="submit-quiz-modal__continue-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emitContinue"
                    >
                        Continue Quiz
                    </PocketButton>
                    <PocketButton
                        class="submit-quiz-modal__submit-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emitSubmit"
                    >
                        Submit Quiz
                    </PocketButton>
                </div>
            </div>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.submit-quiz-modal {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 35px 41px 26px 38px;
    width: 384px;

    &__title {
        outline: none;
        font-weight: 600;
        font-size: 18px;
        line-height: 22px;
        margin: 0 0 12px 0;
    }

    &__text {
        color: $slate-03;
        font-size: 15px;
        line-height: 22px;
        text-align: center;
        margin-bottom: 29px;

        &--dark {
            color: $white;
        }
    }

    &__continue-btn {
        margin-right: 4px;
    }
}

</style>